import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

/**
 * Objects Component - The Complete ProDevConsult Architecture
 * Author: Hazzan
 * Description: A masterclass in Object manipulation, Accessors, and ES6+ Logic.
 */
export default function Objects() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Consolidated Master Content */
    const objectModules = useMemo(() => [
        {
            title: "1. Core Declaration & Modification",
            tag: "Architecture Basics",
            desc: "At ProDevConsult, we prioritize 'const' to maintain reference integrity while allowing property updates and dynamic extensions.",
            code: `// Lead Developer Identity\nconst leadDev = {\n  name: "Hazzan",\n  role: "Architect"\n};\n\n// Modifying existing properties\nleadDev.role = "Senior Solutions Architect";\n\n// Dynamically adding new properties\nleadDev.company = "ProDevConsult";\n\nconsole.log(leadDev.name); // "Hazzan"`
        },
        {
            title: "2. ES6 Shorthand & Computed Names",
            tag: "Clean Code Standard",
            desc: "Efficiency is key. We use shorthand for matching keys and computed names for dynamic object keys.",
            code: `const brand = "ProDevConsult";\nconst corporateInfo = { brand }; // Shorthand\n\n// Computed Property Names\nconst metric = "efficiency";\nconst projectStats = {\n  [metric]: "99.9%",\n  ["client_" + 101]: "Hazzan's VIP"\n};`
        },
        {
            title: "3. Encapsulation: Getters & Setters",
            tag: "Data Validation",
            desc: "Hazzan implements Getters and Setters to ensure data integrity and validation during property assignment.",
            code: `const brandManager = {\n  _brand: "ProDevConsult",\n\n  get brandName() {\n    return this._brand.toUpperCase();\n  },\n\n  set brandName(newName) {\n    if (newName.length > 3) {\n      this._brand = newName;\n    } else {\n      console.error("Invalid Brand Name");\n    }\n  }\n};\n\nbrandManager.brandName = "ProDev";\nconsole.log(brandManager.brandName); // "PRODEV"`
        },
        {
            title: "4. Arrow Functions & Context",
            tag: "Lexical Scope",
            desc: "Arrow functions inherit 'this' from the surrounding context, making them perfect for internal iteration callbacks.",
            code: `const HazzanCar = {\n  model: "Electric Series S",\n  features: ["Autopilot", "FastCharge"],\n  \n  listFeatures() {\n    this.features.forEach(feature => {\n      // 'this' correctly refers to HazzanCar due to arrow function\n      console.log(\`\${this.model}: \${feature}\`);\n    });\n  }\n};`
        },
        {
            title: "5. Immutability & Simulated Destructors",
            tag: "Security & Cleanup",
            desc: "We use Freeze/Seal for security and implement manual cleanup methods to release resources like timers.",
            code: `// Lockdown configuration\nconst config = Object.freeze({ key: "SECURE_99" });\n\n// Cleanup Pattern (Manual Destructor)\nconst monitor = {\n  id: setInterval(() => console.log("Pulse..."), 1000),\n  destroy() {\n    clearInterval(this.id);\n    console.log("Cleanup Complete.");\n  }\n};`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "goldenrod", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    PRODEV OBJECT MASTERCLASS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Enterprise Object Design
                            </Typography>
                            <IconButton onClick={() => handleSpeak("Welcome to the unified ProDevConsult object module. We will cover everything from core declaration to advanced accessors and cleanup patterns.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7, mb: 3 }}>
                            In professional engineering, objects are more than just containers; they are living contracts. At <b>ProDevConsult</b>, Hazzan ensures that every object is secure, self-validating, and memory-efficient.
                        </Typography>
                    </SectionCard>
                </Grid>

                {objectModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">PROCEED TO RECAP</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Global Utilities */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
